/**
 * Deterministic business-process (functional-area) alias normalization.
 *
 * Consulting estimate workbooks abbreviate the standard functional cycles inconsistently
 * (R2R / "Record To Report", O2C, P2P vs "Purchase to Pay", ...). Normalizing these BEFORE any
 * AI pass keeps the canonical vocabulary from fragmenting: the reference-estimate functional-area
 * detector, the discovery grouping inventory, the A6 rename vocabulary, and the import-time
 * canonical merge all fold raw values through here first. Deterministic on purpose - alias
 * folding must never depend on a model call.
 */
/** Canonical name by lower-cased alias. Keys must be lower-case, single-spaced. */
const BUSINESS_PROCESS_ALIASES = {
    r2r: 'Record to Report',
    'record to report': 'Record to Report',
    o2c: 'Order to Cash',
    otc: 'Order to Cash',
    'order to cash': 'Order to Cash',
    p2p: 'Procure to Pay',
    'procure to pay': 'Procure to Pay',
    'purchase to pay': 'Procure to Pay',
    a2d: 'Acquire to Dispose',
    'acquire to dispose': 'Acquire to Dispose',
    'acquisition to disposal': 'Acquire to Dispose',
    h2r: 'Hire to Retire',
    'hire to retire': 'Hire to Retire',
    // NetSuite functional areas (see netsuite-functional-areas.ts) - fold the SuiteX brand names and
    // neutral abbreviation expansions onto the clean area spellings. Deliberately EXCLUDES generic
    // single words another product's corpus could use as its own vocabulary ('billing',
    // 'integrations', 'financials', 'projects', 'analytics', 'commerce', 'platform', 'arm'); foldKey
    // matches the whole trimmed string, so 'hr' / 'wms' / 'ecommerce' are safe neutral expansions.
    wms: 'Warehouse Management',
    'warehouse management system': 'Warehouse Management',
    scm: 'Supply Chain Management',
    'supply chain': 'Supply Chain Management',
    suitebilling: 'Billing',
    'advanced revenue management': 'Revenue Management',
    'revenue recognition': 'Revenue Management',
    revrec: 'Revenue Management',
    'planning & budgeting': 'Planning and Budgeting',
    epm: 'Planning and Budgeting',
    nspb: 'Planning and Budgeting',
    suitepeople: 'Human Resources',
    hr: 'Human Resources',
    hcm: 'Human Resources',
    'human capital management': 'Human Resources',
    psa: 'Professional Services Automation',
    suiteprojects: 'Professional Services Automation',
    openair: 'Professional Services Automation',
    suitecommerce: 'Commerce',
    ecommerce: 'Commerce',
    'e-commerce': 'Commerce',
    suiteanalytics: 'Analytics',
    suitecloud: 'Platform and Integrations',
    fsm: 'Field Service Management',
    'field service': 'Field Service Management',
    oneworld: 'Financial Management',
    'customer relationship management': 'CRM',
};
/** Collapse whitespace and case for alias lookup. */
function foldKey(value) {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
}
/**
 * Normalize one raw business-process / functional-area value: known aliases fold to their
 * canonical name; anything else returns trimmed/whitespace-collapsed as-is (never invents).
 * Empty/blank input returns ''.
 */
export function normalizeBusinessProcessAlias(value) {
    const trimmed = (value ?? '').trim().replace(/\s+/g, ' ');
    if (!trimmed)
        return '';
    return BUSINESS_PROCESS_ALIASES[foldKey(trimmed)] ?? trimmed;
}
/**
 * Match a raw value against a canonical vocabulary: exact (case/whitespace-insensitive) or via
 * alias folding. Returns the canonical entry AS SPELLED IN THE LIST, or undefined when no match -
 * callers use this to tell "already canonical" from "novel value" during import mapping.
 */
export function matchCanonicalBusinessProcess(value, canonical) {
    const normalized = normalizeBusinessProcessAlias(value);
    if (!normalized)
        return undefined;
    const byFold = new Map(canonical.map((c) => [foldKey(c), c]));
    return byFold.get(foldKey(normalized));
}
//# sourceMappingURL=business-process.js.map